import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Global 401 handler: when any authenticated API call returns 401 we treat it
// as a session expiry — show a friendly toast and bounce the user to /login,
// remembering where they were so they land back here after re-auth.
//
// We deliberately skip the redirect for a few benign cases so we don't break
// normal flows:
//   - /auth/login            → wrong creds; surface the error inline
//   - /auth/me               → "is the user logged in?" probe used on first load;
//                              the AuthProvider already handles this gracefully.
//   - /auth/refresh          → background token refresh attempts
//   - /auth/forgot-password  → handled inline by the page
//   - /auth/reset-password   → handled inline by the page
//   - The user is already on /login or another public auth page
// ---------------------------------------------------------------------------
const SKIP_REDIRECT_PATHS = [
  "/auth/login",
  "/auth/me",
  "/auth/refresh",
  "/auth/forgot-password",
  "/auth/reset-password",
];
const PUBLIC_AUTH_ROUTES = ["/login", "/register", "/forgot-password", "/reset-password"];

let sessionExpiryShown = false;

api.interceptors.response.use(
  (response) => {
    // Any 2xx after the guard was raised means a fresh authenticated session
    // — reset so a future expiry triggers the toast + redirect again.
    if (sessionExpiryShown && response?.status >= 200 && response?.status < 300) {
      sessionExpiryShown = false;
    }
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const currentPath = typeof window !== "undefined" ? window.location.pathname : "";

    const shouldSkip =
      status !== 401 ||
      SKIP_REDIRECT_PATHS.some((p) => url.includes(p)) ||
      PUBLIC_AUTH_ROUTES.some((p) => currentPath.startsWith(p));

    if (!shouldSkip && typeof window !== "undefined") {
      // De-dupe: many in-flight requests will all 401 at once. Only show one toast / one redirect.
      if (!sessionExpiryShown) {
        sessionExpiryShown = true;
        try {
          toast.info("Session expired", {
            description: "Please sign in again to continue.",
          });
        } catch {
          /* toast not yet mounted on first paint - ignore */
        }
        // Build a "next" param so the user returns to where they were.
        const next = encodeURIComponent(currentPath + window.location.search);
        // Tiny delay so the toast paints before the navigation tears down React.
        setTimeout(() => {
          window.location.replace(`/login?next=${next}`);
        }, 250);
      }
    }
    return Promise.reject(error);
  }
);

export default api;

export function formatApiError(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function fileUrl(storagePath) {
  if (!storagePath) return "";
  return `${API}/files/${storagePath}`;
}
